import { CdkDragDrop, moveItemInArray } from "@angular/cdk/drag-drop";
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChange } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { ToastrService } from "ngx-toastr";
import { Cost } from "../../../../../models/cost";
import { CostType } from "../../../../../models/cost-type";
import { FormulaCost } from "../../../../../models/formula-cost";
import { CostView } from "../../../../../models/infix-formula/costView";
import { InfixFormula } from "../../../../../models/infix-formula/infix-formula";
import { Registration } from "../../../../../models/registration";
import { CostControllerService } from "../../../../../services/cost-controller/cost-controller.service";
import { CostService } from "../../../../../services/cost-controller/cost.service";
import { CurrencyControllerService } from "../../../../../services/currency-controller/currency-controller.service";
import { FormControllerService } from "../../../../../services/form-controller/form-controller.service";
import { Store } from "../../../../../services/store.service";
import { TranslatedBaseError } from "../../../TranslatedBaseError";
import { TranslateService } from "@ngx-translate/core";
import { RecoverOrphanConfig } from "../../../../../models/recover-orphan-config";

@Component({
  selector: "registration-cost",
  templateUrl: "./cost-registration.component.html",
  styleUrls: ["./cost-registration.component.css"],
  providers: [FormControllerService],
})
export class RegistrationCostComponent extends TranslatedBaseError implements OnInit, OnChanges, OnDestroy {

  @Input() public registration: Registration;
  @Input() public smallView: boolean;
  @Input() public disabled: boolean = false;
  @Input() public determinantMapById: { [key: string]: string };
  @Input() public debugProps: { bugSource: string; bugType: string, debugMode: boolean } = { bugSource: null, bugType: null, debugMode: false };
  @Input() public debugErrors: RecoverOrphanConfig[] = [];
  @Output() public modalClosedEvent = new EventEmitter();
  public CostType: any = CostType;
  public editedRegistrationId: boolean;
  public costs: Cost[] = [];
  public registrationCostListLength: number;
  public registrationCostToDelete: any;
  public showDeterminant: boolean[] = [];
  public currencies: any[] = [];
  public regCostCurrencies = {};
  public cost: Cost;
  public costView: CostView = new CostView();
  public addNew: boolean = false;
  public showDeterminantBuilder: boolean = false;
  public showCosts = {};
  public showList: boolean = false;
  public serviceId: string;

  public isCollapsed: boolean = false;

  public infixFormula: InfixFormula;

  public subscription: any;

  public readonly containerTypes: string[] = ["panel", "tabs", "tab", "columns", "column", "table", "editgrid"];

  constructor(private costControllerService: CostControllerService,
    private formControllerService: FormControllerService,
    private costService: CostService,
    public store: Store,
    public currencyControllerService: CurrencyControllerService,
    private route: ActivatedRoute,
    public toastr: ToastrService, public translateService: TranslateService) {
    super(toastr, translateService);
  }

  public ngOnInit() {
    this.route.params.subscribe((params) => {
      this.serviceId = params.serviceId;
    });
    this.costView.value = true;
    this.getCurrencies();
    this.subscription = this.costService.update.subscribe((data: any) => {
      if (data.update) {
        this.loadCosts();
      }
    });
  }

  public ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  public ngOnChanges(changes: { [propName: string]: SimpleChange }) {
    if (changes.registration && changes.registration.currentValue.id && (!changes.registration.previousValue
      || (changes.registration.currentValue.id !== changes.registration.previousValue.id))) {
      return this.loadFormFieldGroup(this.loadCosts);
    }
    this.loadCosts();
  }

  public loadFormFieldGroup(cb) {
    this.formControllerService.getFilteredFormFields(this.serviceId, "number,datetime,datagrid,editgrid,catalog", false, false, false, false, false, false, false).subscribe(
      (fdata) => {
        fdata.forEach((item) => {
          item.title = item.fullName;
          item.id = item.fullKey || item.key;
          item.value = item.fullkey || item.key;
          this.costView.formFieldsGroup.push(item);
        });
        this.costView.formFieldsGroup.forEach((ffg) => {
          if (!this.costView.properties.find((prop) => prop.id === ffg.id)) {
            this.costView.properties.push({ id: ffg.id, name: ffg.id, label: ffg.title, compareById: true });
          }
        });
        return cb.call(this);
      });
  }

  public dropped(event: CdkDragDrop<Cost[]>) {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);

      // component logic
      if (!this.disabled) {
        this.costs.forEach((it, index, itemsArray) => {
          itemsArray[index].sortOrder = index;
        });
        this.sortCostList();
      }
    }
  }

  private sortCostList() {
    this.costControllerService.sortCosts(this.registration.id, this.costs).subscribe(
      (data) => this.responseSuccessful("Costs reordered!"),
      (error) => this.showErrorMessageWithTitle("Error HTTP Post Service", error),
    );
  }

  public loadCosts() {
    if (this.registration && this.registration.id) {
      this.costControllerService.getRegistrationCostList(this.registration.id).subscribe(
        (data) => {
          this.costs = data.sort((a, b) => a.sortOrder - b.sortOrder);
          this.costs.forEach((thisCost: Cost) => {
            if (thisCost.costType === CostType.FORMULA) {
              if ((thisCost as FormulaCost).formulaCostItems) {
                (thisCost as FormulaCost).formulaCostItems.sort((a, b) => {
                  return a.sortOrder - b.sortOrder;
                });
                this.costView.reloadPrettyPrint(thisCost as FormulaCost, this.translateService);
              }
            }
            this.currencies.forEach((currency) => {
              if (thisCost.currencyId === currency.id) {
                thisCost.currency = currency;
              }
            });
          });
        },
        (error) => this.showErrorMessage(error),
      );
    }
  }

  public updateData() {
    this.showList = false;
    this.showDeterminant = new Array(this.costs.length).fill(false);
    this.registrationCostListLength = this.costs.length;
    this.toggleRegistrationCostsFromCostsList();
    this.setRegistrationCostsCurrency();
  }

  public toggleRegistrationCostsFromCostsList() {
    this.showCosts = {};
    for (let j = this.costs.length - 1; j >= 0; j--) {
      this.showCosts[this.costs[j].id] = true;
      for (let i = this.costs.length - 1; i >= 0; i--) {
        if (this.costs[j].id === this.costs[i].id) {
          this.showCosts[this.costs[j].id] = false;
        }
      }
    }
  }

  public setRegistrationCostsCurrency() {
    this.costs.forEach((cost) => {
      this.currencies.forEach((currency) => {
        if (cost.currencyId === currency.id) {
          this.regCostCurrencies[cost.id] = currency;
        }
      });
    });
  }

  public hasDeterminant(item: Cost) {
    if (!item.jsonDeterminants || item.jsonDeterminants === '[{"type":"OR","items":[]}]') {
      return false;
    }
    return true;
  }

  public removeDeterminant(cost: Cost, index: any) {
    if (!this.disabled) {
      cost.jsonDeterminants = "";
      this.costControllerService.saveCost(cost).subscribe(
        () => {
          this.showDeterminant[index] = false;
          this.responseSuccessful("Cost Registration is updated");
        },
        (error) => this.showErrorMessageWithTitle("Error HTTP Post Service!", error),
      );
    }
  }

  public hasError(id: string): boolean {
    if (this.debugProps.debugMode && this.debugProps.bugSource === id) {
      return true;
    }
    return this.debugErrors && this.debugErrors.some((error) => error.objectId === id);
  }

  public getCurrencies() {
    this.currencyControllerService.getCurrencies().subscribe(
      (data) => {
        this.currencies = data;
        this.costs.forEach((thisCost: Cost) => {
          this.currencies.forEach((currency) => {
            if (thisCost.currencyId === currency.id) {
              thisCost.currency = currency;
            }
          });
        });
      },
      (error: any) => this.showErrorMessage(error),
    );
  }

  public openEdit(cost: Cost, determinantView = false) {
    if (!this.disabled) {
      this.showDeterminantBuilder = determinantView;
      if (cost) {
        this.cost = cost;
        if (this.costs && this.costs.length > 0) {
          this.costs.forEach((thisCost: any) => {
            if (thisCost.id === cost.id) {
              this.cost = thisCost;
            }
          });
        }
        this.addNew = false;
      } else {
        this.cost = new Cost();
        this.addNew = true;
      }
      this.updateEditData();
    }
  }

  public deleteCost(isConfirmed: boolean, costId: string, index: number) {
    if (!this.disabled && isConfirmed) {
      this.costControllerService.deleteCost(costId).subscribe(
        () => this.costs.splice(index, 1),
      );
    }
  }

  public showOrAdd() {
    if (!this.disabled) {
      if (this.costs.length > 0 && this.registrationCostListLength !== this.costs.length) {
        this.showList = !this.showList;
      } else {
        this.openEdit(null, false);
      }
    }
  }

  public updateEditData() {
    if (!this.disabled) {
      this.showList = false;
      this.costService.serviceId = this.serviceId;
      this.costService.registration = this.registration;
      this.costService.cost = JSON.parse(JSON.stringify(this.cost)); // mini-hack so the list doesn't update when working in edit layer
      this.costService.costView = this.costView;
      this.costService.addNew = this.addNew;
      this.costService.showDeterminantBuilder = this.showDeterminantBuilder;
      this.costService.initEdit();
      this.store.state.common.sections.type = "cost";
    }
  }

}
